import { useEffect, useRef, useState } from "react";
import { FaUmbrellaBeach } from "react-icons/fa6";
import {
  IoArrowBack,
  IoClose,
  IoSearch,
} from "react-icons/io5";
import { searchActivity as searchActivityApi } from "@/services/api/activities";
import {
  getSearchGroups,
  SEARCH_DEBOUNCE_MS,
  SEARCH_GROUP_LABELS,
  SEARCH_MIN_CHARS,
} from "@/utils/activitySearch";
import styles from "../HolidayListing/HolidayListing.module.css";

const GROUP_ORDER = ["cities", "countries", "activities"];

function SearchGroupSection({ title, items, onSelect, asList = false }) {
  if (!items.length) return null;

  if (!asList) {
    return (
      <div className={styles.locationSearchGroup}>
        {title ? <p className={styles.locationSearchGroupTitle}>{title}</p> : null}
        <div className={styles.locationSearchChips}>
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              className={styles.locationSearchChip}
              onClick={() => onSelect(item)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.locationSearchGroup}>
      {title ? <p className={styles.locationSearchGroupTitle}>{title}</p> : null}
      <div className={styles.locationSearchResults}>
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            className={styles.locationSearchResult}
            onClick={() => onSelect(item)}
          >
            <span className={styles.locationSearchResultIcon}>
              <FaUmbrellaBeach aria-hidden="true" />
            </span>
            <span className={styles.locationSearchResultText}>
              <strong>{item.title}</strong>
              <small>{item.subtitle}</small>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function LocationSearchScreen({ initialQuery, onClose, onSelect }) {
  const [query, setQuery] = useState(initialQuery || "");
  const [groups, setGroups] = useState({
    cities: [],
    countries: [],
    activities: [],
  });
  const [loading, setLoading] = useState(true);
  const requestRef = useRef(0);

  useEffect(() => {
    setQuery(initialQuery || "");
  }, [initialQuery]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    searchActivityApi()
      .then((response) => {
        if (cancelled) return;
        setGroups(getSearchGroups(response));
      })
      .catch(() => {
        if (cancelled) return;
        setGroups({ cities: [], countries: [], activities: [] });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const trimmed = query.trim();

    if (trimmed.length < SEARCH_MIN_CHARS) {
      setLoading(false);
      return undefined;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);

    const timer = window.setTimeout(async () => {
      try {
        const response = await searchActivityApi(trimmed);
        if (requestRef.current !== requestId) return;
        setGroups(getSearchGroups(response));
      } catch {
        if (requestRef.current !== requestId) return;
        setGroups({ cities: [], countries: [], activities: [] });
      } finally {
        if (requestRef.current === requestId) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [query]);

  const showDefault = query.trim().length < SEARCH_MIN_CHARS;
  const hasResults = GROUP_ORDER.some((key) => groups[key]?.length);

  return (
    <div className={styles.locationSearchRoot}>
      <header className={styles.locationSearchHeader}>
        <button type="button" className={styles.locationSearchBackBtn} onClick={onClose} aria-label="Back">
          <IoArrowBack />
        </button>
        <div className={styles.locationSearchInputWrap}>
          <IoSearch className={styles.locationSearchInputIcon} aria-hidden="true" />
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search"
            className={styles.locationSearchInput}
            autoFocus
            autoComplete="off"
          />
          {query ? (
            <button
              type="button"
              className={styles.locationSearchClearBtn}
              onClick={() => setQuery("")}
              aria-label="Clear search"
            >
              <IoClose />
            </button>
          ) : null}
        </div>
      </header>

      <div className={styles.locationSearchBody}>
        {showDefault ? (
          <div className={styles.locationSearchEmptyArt}>
            <img src="/assets/icons/SearchVector.svg" alt="" />
          </div>
        ) : null}

        {loading ? <p className={styles.locationSearchStatus}>Loading...</p> : null}

        {!loading && !showDefault && !hasResults ? (
          <p className={styles.locationSearchStatus}>No results found.</p>
        ) : null}

        {/* {!loading && hasResults && showDefault
          ? GROUP_ORDER.map((key) => (
              <SearchGroupSection
                key={key}
                title={SEARCH_GROUP_LABELS[key]}
                items={groups[key] || []}
                onSelect={onSelect}
              />
            ))
          : null} */}

        {!loading && hasResults && !showDefault
          ? GROUP_ORDER.map((key) => (
              <SearchGroupSection
                key={key}
                title={SEARCH_GROUP_LABELS[key]}
                items={groups[key] || []}
                onSelect={onSelect}
                asList
              />
            ))
          : null}
      </div>
    </div>
  );
}
